export const CurrencySymbols = {
  USD: "$", // US Dollar
  EUR: "€", // Euro
  CRC: "₡", // Costa Rican Colón
  GBP: "£", // British Pound Sterling
  ILS: "₪", // Israeli New Sheqel
  INR: "₹", // Indian Rupee
  JPY: "¥", // Japanese Yen
  KRW: "₩", // South Korean Won
  NGN: "₦", // Nigerian Naira
  PHP: "₱", // Philippine Peso
  PLN: "zł", // Polish Zloty
  PYG: "₲", // Paraguayan Guarani
  THB: "฿", // Thai Baht
  UAH: "₴", // Ukrainian Hryvnia
  VND: "₫", // Vietnamese Dong
  CHF: "CHF", // Schweizer Franken
  HUF: "Ft", // Hungarian Forint
  AUD: "A$", // Australian Dollar
  CAD: "C$", // Canadian Dollar
  CNY: "¥", // Chinese Yuan (also uses ￥)
  DKK: "kr", // Danish Krone
  HKD: "HK$", // Hong Kong Dollar
  NOK: "kr", // Norwegian Krone
  NZD: "NZ$", // New Zealand Dollar
  SEK: "kr", // Swedish Krona
  SGD: "S$", // Singapore Dollar
  ZAR: "R", // South African Rand
  BRL: "R$", // Brazilian Real
  MXN: "$", // Mexican Peso (often shown as MX$)
  AED: "د.إ", // UAE Dirham
  SAR: "﷼", // Saudi Riyal
  RUB: "₽", // Russian Ruble
  TRY: "₺", // Turkish Lira
} as const;

export const CurrencyDecimals = {
  // Standard 2-decimal currencies
  USD: 2, // US Dollar
  EUR: 2, // Euro
  GBP: 2, // British Pound Sterling
  ILS: 2, // Israeli New Sheqel
  INR: 2, // Indian Rupee
  CNY: 2, // Chinese Yuan
  DKK: 2, // Danish Krone
  HKD: 2, // Hong Kong Dollar
  NOK: 2, // Norwegian Krone
  NZD: 2, // New Zealand Dollar
  SEK: 2, // Swedish Krona
  SGD: 2, // Singapore Dollar
  ZAR: 2, // South African Rand
  BRL: 2, // Brazilian Real
  MXN: 2, // Mexican Peso
  AED: 2, // UAE Dirham
  SAR: 2, // Saudi Riyal
  RUB: 2, // Russian Ruble
  TRY: 2, // Turkish Lira
  CAD: 2, // Canadian Dollar
  AUD: 2, // Australian Dollar
  PLN: 2, // Polish Zloty
  THB: 2, // Thai Baht
  PHP: 2, // Philippine Peso
  UAH: 2, // Ukrainian Hryvnia
  CRC: 2, // Costa Rican Colón (electronic payments commonly 2)
  CHF: 2, // Swiss Franc (cash may round to 0.05; keep 2 for cards)
  NGN: 2, // Nigerian Naira

  // Zero-decimal (practical usage) - Exceptions, differ from ISO 4217 standard
  JPY: 0, // Japanese Yen
  KRW: 0, // South Korean Won
  VND: 0, // Vietnamese Dong
  HUF: 0, // Hungarian Forint (no fillér in circulation)
  PYG: 0, // Paraguayan Guarani

  // Three-decimal
  BHD: 3, // Bahraini Dinar
  OMR: 3, // Omani Rial
} as const;

export type CurrencyCode = keyof typeof CurrencySymbols;
export type CurrencySymbol = (typeof CurrencySymbols)[CurrencyCode];

export const getCurrencySymbolOrDefault = (currencyCode?: CurrencyCode | null): CurrencySymbol => {
  if (!currencyCode) {
    return CurrencySymbols.EUR;
  }

  return CurrencySymbols[currencyCode] || CurrencySymbols.EUR;
};

/**
 * Gets the divisor for a currency based on its decimal places.
 * @param {CurrencyCode} currency - The currency code
 * @returns {number} The divisor (e.g., 100 for EUR, 1 for HUF)
 */
export const getCurrencyDivisor = (currency: CurrencyCode): number => {
  const decimals = CurrencyDecimals[currency] ?? CurrencyDecimals.EUR;
  return Math.pow(10, decimals);
};

/**
 * Gets the appropriate locale for number formatting.
 * Maps i18n language codes to locale identifiers for Intl.NumberFormat.
 */
const getFormattingLocale = (locale?: string): string => {
  const currentLocale = locale || "de-DE";

  // Map language codes to appropriate locales for number formatting
  const localeMap: Record<string, string> = {
    hu: "hu-HU", // Hungarian: 1 500 Ft (space as thousands separator)
    de: "de-DE", // German: 1.500,50 € (dot thousands, comma decimal)
    en: "en-US", // English: 1,500.50 $ (comma thousands, dot decimal)
    fr: "fr-FR", // French: 1 500,50 € (space thousands, comma decimal)
    es: "es-ES", // Spanish: 1.500,50 € (dot thousands, comma decimal)
    it: "it-IT", // Italian: 1.500,50 € (dot thousands, comma decimal)
    pl: "pl-PL", // Polish: 1 500,50 zł (space thousands, comma decimal)
    tr: "tr-TR", // Turkish: 1.500,50 ₺ (dot thousands, comma decimal)
    da: "da-DK", // Danish: 1.500,50 kr (dot thousands, comma decimal)
    fi: "fi-FI", // Finnish: 1 500,50 € (space thousands, comma decimal)
    nl: "nl-NL", // Dutch: 1.500,50 € (dot thousands, comma decimal)
  };

  return localeMap[currentLocale] || "de-DE"; // Default to German
};

export const formatAmountForCurrency = (amount: string, currency: CurrencyCode = "EUR"): number | null => {
  const parsedAmount = parseFloat(amount);
  if (isNaN(parsedAmount)) return null;

  const divisor = getCurrencyDivisor(currency);
  return parsedAmount / divisor;
};

/**
 * Formats a price amount without a currency symbol.
 *
 * @param {number} amount - The raw amount from backend
 * @param {CurrencyCode} [currency='EUR'] - The currency code to determine decimal places
 * @returns {string|null} Formatted price without currency symbol (e.g., "10,50", "1,050")
 */
export const formatPrice = (amount: number, currency: CurrencyCode = "EUR"): string | null => {
  if (isNaN(amount)) return null;

  const divisor = getCurrencyDivisor(currency);
  const value = amount / divisor;
  const decimals = CurrencyDecimals[currency] ?? CurrencyDecimals.EUR;

  const formattingLocale = getFormattingLocale();

  try {
    const formatter = new Intl.NumberFormat(formattingLocale, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
    return formatter.format(value);
  } catch (error) {
    // Fallback to German-style formatting
    return value.toFixed(decimals).replace(/\./g, ",");
  }
};
